// Admin entry point — Vite module for backend pages
// Legacy JS modules are migrated here incrementally.

import { TagBoxCoordMapper } from './mediaTagging/TagBoxCoordMapper.js';
import { TagBoxModel } from './mediaTagging/TagBoxModel.js';
import { TagChangeDetector } from './mediaTagging/TagChangeDetector.js';
import { TagBoxBounds } from './mediaTagging/TagBoxBounds.js';
import { TaggingCore } from './mediaTagging/TaggingCore.js';
import { structuredTagsToTags } from './mediaTagging/structuredTags.js';
import { createQuicklistSource } from './keywordAutocomplete/quicklistSource.js';   // P12 4b: source for the new tag field
import { createTaggingApiClient } from './mediaTagging/TaggingApiClient.js';         // Tag-AJAX-Transport-Naht (get_tags + admin_changetag)

import { TopoMapCoreBackend } from './topoMapCore/TopoMapCoreBackend.js';
import { GoogleMapsAdapter } from './topoMapCore/adapter/GoogleMapsAdapter.js';
import { MapCanvasManager } from './topoMapCore/tools/MapCanvasManager.js';
import { mediator } from './mediator/mediatorInstance.js';
import { createOverlayRowsView } from './view/overlay/OverlayRowsView.js';
import { createListNav } from './view/listNav.js';
import { createListPanel } from './view/listPanel.js';
import { createStatusLine } from './view/statusLine.js';
import { createVideoControls, createVideoSeekBar } from './view/videoControls.js';
import { createAudioControls } from './view/audioControls.js';
import { str } from './i18n/str.js';
import { lockWheel } from './userInput/wheelLock.js';
import { onEscape } from './userInput/escapeKey.js';
import { compactWhenCramped } from './view/compactWhenCramped.js';
import { parseDateText } from './dateText/parseDateText.js';
import { createOverlayUploader } from './upload/OverlayUploader.js';
import { createOverlayGateway } from './overlayAdmin/OverlayGateway.js';
import { createOverlayDirtyTracker } from './overlayAdmin/OverlayDirtyTracker.js';
import { createOverlayState } from './overlayAdmin/OverlayState.js';
import { createOverlayAdminController } from './overlayAdmin/OverlayAdminController.js';
import { enhanceOverlayInfoText } from './overlayAdmin/infoTextEditor.js';
import { OVERLAY_EVENTS } from './overlayAdmin/overlayEvents.js';
import { createMapUiReconciler } from './topoMapCore/tools/MapUiReconciler.js';
import { MapStateStore, createDefaultMapState } from './topoMapCore/state/MapStateStore.js';
import { LocationShapeLayer } from './topoMapCore/marker/LocationShapeLayer.js';
import { createLocationMarkerIcons } from './topoMapCore/marker/locationMarkerIcons.js';
import { createInfoPopovers } from './view/infoPopover.js';
import { createTabs } from './view/tabs.js';
import { createFieldReset } from './view/fieldReset.js';
import { createFilterViewMounter } from './filterView/mountFilterViews.js';
import { createDocIdFieldCheck } from './docIdInput/docIdFieldCheck.js';
import { mountQuickReport } from './messageCenter/quickReport.js';
import { mountInboxButton } from './messageCenter/inboxButton.js';
import { createNoticeOverlay } from './messageCenter/noticeOverlay.js';
import { createNoticeWindows } from './messageCenter/noticeWindows.js';
import { createMessageCenterApi } from './messageCenter/messageCenterApi.js';

console.log('[Topothek Admin] Vite module loaded');

// Admin-Karten (Verorten/Einstellungen, Legacy-Bootstrap via document_locate.js/preferences.js): dieselbe deklarative
// Karten-UI wie Frontend main/gallery über den MapUiReconciler (mapType oben links, Overlay-Button oben rechts,
// topo2-Zoom/Neigen). IDEMPOTENT pro Karte — Reconciler + per-Kontext-Stores auf der Karten-Instanz gecached, damit
// Pool-Reuse (BackEnd.acquireMap) keine Dubletten baut. Mini-api: getStore (per Kontext) + toggleOverlayPanel (lazy übers
// overlayPanel-Modul-Handle). main/gallery-Aktionen (Suchkreis/Archive) stehen in den Admin-Kontexten NICHT im Katalog → Stubs.
function setupMapUi(google, map, opts = {}) {
    const context = opts.context || 'locate';
    if (!map.__topoAdminMapUi) {
        const stores = {};
        const getStore = (ctx) => stores[ctx] || (stores[ctx] = new MapStateStore(createDefaultMapState(ctx)));
        let reconciler = null;
        const api = {
            getStore,
            toggleOverlayPanel: () => { const h = reconciler && reconciler.getModule('overlayPanel'); if (h && h.toggle) { h.toggle(); } },
            isGallery: () => false,
            publish: () => {}, subscribe: () => {},
            setSearchCircleLeftClick: () => {}, clearSearchCircle: () => {}
        };
        reconciler = createMapUiReconciler(google, api);
        map.__topoAdminMapUi = { reconciler, api };
    }
    map.__topoAdminMapUi.reconciler.reconcile(map, context);
    return map.__topoAdminMapUi;
}

// BackEnd-Dirigent über dem geteilten topoMapCore-Kern (Option X: eigene Admin-Fassade neben dem
// Frontend-`window.ModuleJS`; Front- und Admin-Bundle laufen nie auf derselben Seite).
// v1: Karten-Pool für Admin-Konsumenten (Locate-Popup, später Heimat-Bereich) + Mediator für die
// lose Legacy↔Modular-Naht während der Migration.
window.ModuleJS = {
    ...(window.ModuleJS || {}),
    // mediaTagging modules (feature branch)
    TagBoxCoordMapper,
    TagBoxModel,
    TagChangeDetector,
    TagBoxBounds,
    TaggingCore,
    // Konverter: get_document_tags-Struktur → Tag-Objekte {…, text, hidden} für mountTagBox + Editor.
    structuredTagsToTags,
    // P12 4b: contenteditable-Keyword-Autocomplete-Quelle (quicklist) für das neue Tag-Feld.
    createQuicklistSource,
    // Tag-AJAX-Transport-Naht (get_tags + admin_changetag) — dünner Client, einzige Anlaufstelle fürs Backend.
    tagApi: createTaggingApiClient(),
    // BackEnd / topoMapCore core + overlay admin (master)
    BackEnd: TopoMapCoreBackend,
    Mediator: mediator,
    // Bausteine des geteilten Kerns (bei direktem Bedarf):
    GoogleMapsAdapter,
    MapCanvasManager,
    // View-Schicht (Admin-UI, google-frei): rendert die Overlay-Listenzeilen aus reinen Daten.
    createOverlayRowsView,
    // Generischer listNav (◄ [Zentrum] ►, Dropdown) — Consumer: PDF-Seiten-Nav im documents-Popup.
    createListNav,
    // Generisches Panel (Header + Liste + Schließkreuz) — Consumer: Tag-Liste im documents-Popup.
    createListPanel,
    // Eine Meldungszeile (Erfolg blendet aus, Fehler bleibt bis ×) — Consumer: Tagging-Status im documents-Popup.
    createStatusLine,
    // Eigene Video-Steuerung (Fußleiste + Slider über dem Video) — Consumer: Video im documents-Popup.
    createVideoControls,
    createVideoSeekBar,
    // Own audio controls in the look of the video/PDF controls — Consumer: basic.js createAudioPlayer (documents list, group).
    createAudioControls,
    // i18n: Key → Text mit Rückfall (Sprache → EN → DE → Key) — Consumer: documents.js (Tagging-Texte, Labels an die Views).
    // Übergang bis zum Merge von feat/translation_boost (siehe i18n/str.js).
    str,
    // userInput: the popup swallows the mouse wheel; lists marked js-wheel-scroll scroll themselves.
    lockWheel,
    // userInput: Escape as close/back key (ignored while typing) — available for backend windows, too.
    onEscape,
    // A button turns compact (class is-compact) when it is squeezed below its own label — Consumer: popup footer close button.
    compactWhenCramped,
    // Date text → from/to range (pure) — Consumer: documents.js extractDate (date_text field, footer bulk fill).
    parseDateText,
    // Upload-Schicht (Plupload-CORE + Upload-Bereich-UI): meldet Abschluss als Bus-Event.
    createOverlayUploader,
    // Persistenz-Seam (AJAX-Transport): Endpunkte/Action-Namen an einer Stelle.
    createOverlayGateway,
    // Dirty-Tracking (Baselines + Feld-/Zeilen-Markierung + Σ ungespeichert).
    createOverlayDirtyTracker,
    // In-Memory-State (Schnitt 4): Auswahl + Editor-Registry.
    createOverlayState,
    // Controller-Orchestrierung (Schnitt 5): Toolbar + Lifecycle-/Persistenz-Funktionen.
    createOverlayAdminController,
    // Progressive Enhancement: overlay info_text-Textareas → topoEditor (Links + Hover-Edit). Textarea bleibt Anker.
    enhanceOverlayInfoText,
    // Einzige Quelle der Overlay-Bus-Event-Namen (Außen-Naht Legacy↔Module).
    overlayEvents: OVERLAY_EVENTS,
    // Admin-Karten-UI (Verorten/Einstellungen): deklarative Bedien-UI (mapType/Zoom/Neigen/Overlay-Button) über den Reconciler.
    setupMapUi,
    // Verortungs-Grafik als Canvas-OverlayView im floatPane (über den Overlays) — Schritt 1: parallel zur Legacy-Zeichnung.
    LocationShapeLayer,
    // Cyan-SVG-Icons für die Verortungs-Marker (Zentrum-Ring / Fähnchen-Punkt / Move-Handle) — ersetzen die cyan PNGs
    // vektorscharf; Farbe = Legacy-Locate-Cyan (leuchtet auf mehr Untergründen als der Akzent), Form bleibt akzentfarben.
    createLocationMarkerIcons,
};

// Info buttons ([data-info-popover], e.g. the search rules in the documents.php filter) on every backend page:
// one delegated listener, so markup that is replaced later (ajax filter table) needs no setup (plan documents_admin-search).
createInfoPopovers();

// Tabs ([data-tabs]) on every backend page, e.g. "Admin-Filter" / "Frontpage-Suche" in the documents.php filter; and the
// reset of tab 2 there — the legacy reset (documents.js #filter_reset) only knows the selects of tab 1.
createTabs();
createFieldReset({ trigger: '#filter_reset' });

// The filter views of documents.php (superuser tabs, plan documents_admin-search): mounted wherever the page carries
// [data-filter-view-state], again after every AJAX replace of the filter table; a no-op on other backend pages.
createFilterViewMounter();

// The document id field of the documents.php filter (#filterDocID): an input that fits no field rule is not sent
// (chef list Nr. 6a); a no-op on pages without the form.
createDocIdFieldCheck();

// Message center quick report (bigplan backend/message-center, phase quick-report): the button in the admin header
// (#quickReport, only for users with ticketCreate) opens the report form over the page; a no-op without the container.
mountQuickReport();

// Message center inbox button (bigplan backend/message-center, step 8b): envelope with the unread counter in the admin
// header (#inboxButton, only with pageInbox); asks the summary after load, every 5 minutes and on return to the tab.
// The pending notices of each answer go to the overlay (step 8c) and the notice window (step 8d; it also brings back
// the window of the last page); built only where the button is.
if (document.getElementById('inboxButton')) {
    const inboxApi = createMessageCenterApi();
    const noticeOverlay = createNoticeOverlay({ api: inboxApi });
    const noticeWindows = createNoticeWindows({ api: inboxApi });
    noticeWindows.restore();
    mountInboxButton({ api: inboxApi, onSummary: (summary) => {
        noticeOverlay.show(summary.pending);
        noticeWindows.showPending(summary.pending);
    } });
}
